import { useEffect, useRef } from "react";
import { PackageDetails, ServiceInformations } from "../../types/serviceDetail";
import { A11y, Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import { Swiper, SwiperSlide } from "swiper/react";
import ServiceRequiredDoc from "./ServiceRequiredDoc";
import ServiceInformation from "./ServiceInformation";

interface ServiceInformationSection {
  details: ServiceInformations[];
  title: string | null;
  line: string | null;
}
interface PackageSectionProps {
  type?: string;
  packageDetails?: PackageDetails[];
  sectionTitle: string | null;
  line?: string | null;
  selectedService: PackageDetails | null;
  setSelectedService: (service: PackageDetails | null) => void;
  serviceInformation: ServiceInformationSection;
  isPage?: boolean | false;
}

const PackageSection = ({
  isPage = false,
  serviceInformation,
  type,
  packageDetails = [],
  sectionTitle,
  line,
  selectedService,
  setSelectedService,
}: PackageSectionProps) => {
  const serviceSelectedRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    import("./PackageSection.css");
  }, []);

  useEffect(() => {
    if (!selectedService && packageDetails.length > 0) {
      setSelectedService(packageDetails[0]);
    }
  }, [packageDetails, selectedService]);
  const requireDocs = selectedService?.requireDocs;

  const importantDocs = {
    insideDescription:
      requireDocs?.requiredDocs
        .filter((doc) => doc.isImportant)
        .map((doc) => doc.TagLine)
        .join("---") || "",
  };

  const nonImportantDocs = {
    featureDocs: requireDocs?.featureDocs ?? [],
    requiredDocs:
      requireDocs?.requiredDocs.filter((doc) => !doc.isImportant) ?? [],
  };

  const handleClick = (service: PackageDetails) => {
    if (selectedService?.RecordId !== service.RecordId) {
      setSelectedService(
        selectedService?.RecordId === service.RecordId
          ? selectedService
          : service
      );
    }
    setTimeout(() => {
      serviceSelectedRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };
  return (
    <div className="container">
      <div className="row">
        {type === "package" ? (
          <>
            {packageDetails && (
              <div className="my-3 p-4 bg-2 rounded-3">
                <div className="container mt-4">
                  <p className="text-start font-14 text-black ff-semibold mb-0">
                    {packageDetails[0].PackageName}
                  </p>
                  <p className="text-start font-14 text-primary ff-semibold">
                    <span className="ff-currency ff-medium font-16"></span>
                    {packageDetails[0].totalPrice}
                  </p>
                  <div className="row">
                    <div className="timeline-container">
                      {packageDetails[0].packageOptions.map(
                        (pkOption, index) => (
                          <div
                            key={`pk-options-${pkOption.PriceRecordId}`}
                            className="timeline-card layout-border"
                          >
                            {/* Circle with Number */}
                            <div className="timeline-step-circle">
                              {index + 1}
                            </div>
                            <div className="d-flex align-items-center justify-content-between gap-2">
                              <div className="w-75">
                                <div
                                  className={`timeline-title font-12 ff-semibold mb-2 serv-${pkOption.PriceRecordId}`}
                                >
                                  <div className="font-10 ff-bold">
                                    {pkOption.serviceName}
                                  </div>
                                </div>
                                <p className="font-14 ff-bold text-black mb-1">
                                  <span className="me-1">
                                    <svg
                                      width="17"
                                      height="17"
                                      viewBox="0 0 17 17"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M8.49987 0C9.16106 4.01899e-05 9.79961 0.240893 10.2961 0.677528L10.427 0.800792L11.0204 1.39416C11.1833 1.55594 11.3959 1.65808 11.624 1.68404L11.7387 1.69085H12.5888C13.2836 1.69081 13.952 1.95656 14.457 2.43361C14.962 2.91066 15.2654 3.56285 15.3049 4.25644L15.3092 4.41116V5.26126C15.3092 5.49078 15.3874 5.71436 15.5285 5.89288L15.605 5.97789L16.1975 6.57126C16.6886 7.05959 16.975 7.71687 16.9982 8.40906C17.0214 9.10125 16.7798 9.77624 16.3225 10.2964L16.1992 10.4273L15.6058 11.0207C15.4441 11.1835 15.3419 11.3962 15.316 11.6242L15.3092 11.739V12.5891C15.3092 13.2838 15.0434 13.9522 14.5664 14.4572C14.0893 14.9623 13.4371 15.2657 12.7436 15.3052L12.5888 15.3094H11.7387C11.5095 15.3095 11.287 15.3868 11.1071 15.5287L11.0221 15.6052L10.4287 16.1978C9.94041 16.6889 9.28313 16.9752 8.59094 16.9985C7.89875 17.0217 7.22376 16.78 6.70361 16.3227L6.5727 16.1995L5.97933 15.6061C5.81648 15.4443 5.60383 15.3422 5.37576 15.3162L5.261 15.3094H4.4109C3.71618 15.3095 3.04778 15.0437 2.54275 14.5667C2.03772 14.0896 1.73435 13.4374 1.69483 12.7438L1.69058 12.5891V11.739C1.69051 11.5098 1.61325 11.2873 1.47126 11.1074L1.39475 11.0224L0.802231 10.429C0.311114 9.94067 0.0247513 9.28339 0.00153081 8.5912C-0.0216897 7.89901 0.219981 7.22403 0.677267 6.70387L0.800531 6.57296L1.3939 5.97959C1.55568 5.81675 1.65782 5.60409 1.68378 5.37602L1.69058 5.26126V4.41116L1.69483 4.25644C1.73279 3.58949 2.01483 2.95983 2.4872 2.48746C2.95957 2.01509 3.58923 1.73305 4.25618 1.6951L4.4109 1.69085H5.261C5.4902 1.69077 5.71269 1.61351 5.89262 1.47152L5.97763 1.39501L6.571 0.802493C6.82376 0.548219 7.1243 0.346419 7.45533 0.208699C7.78635 0.0709776 8.14134 5.19206e-05 8.49987 0ZM11.6427 6.19041C11.4833 6.03105 11.2671 5.94152 11.0417 5.94152C10.8162 5.94152 10.6001 6.03105 10.4406 6.19041L7.64127 8.98894L6.54209 7.89061L6.46218 7.82005C6.29132 7.68794 6.07658 7.62581 5.86157 7.64629C5.64656 7.66677 5.44741 7.76833 5.30456 7.93033C5.16172 8.09233 5.08589 8.30262 5.09248 8.51851C5.09907 8.73439 5.18759 8.93967 5.34005 9.09265L7.04025 10.7928L7.12016 10.8634C7.28372 10.9903 7.48796 11.0531 7.69456 11.0401C7.90115 11.0271 8.09591 10.9392 8.24229 10.7928L11.6427 7.39245L11.7132 7.31254C11.8401 7.14898 11.903 6.94474 11.89 6.73815C11.877 6.53155 11.789 6.3368 11.6427 6.19041Z"
                                        fill="#385986"
                                      />
                                    </svg>
                                  </span>{" "}
                                  {pkOption.sub2ServiceName}
                                </p>
                              </div>
                              <div className="bg-primary p-1 px-1 w-25 text-white rounded-3 font-10 d-flex align-items-center justify-content-center">
                                <span className="me-2">
                                  <svg
                                    width="10"
                                    height="6"
                                    viewBox="0 0 10 6"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M1 3L3.66667 5.5L9 0.5"
                                      stroke="white"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </span>
                                selected
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="modal-service-card p-0">
              <div className="hd-am pt-3">
                <h2 className="text-center font-18 font-sm-14 text-black ff-bold">
                  {selectedService
                    ? selectedService.Name
                    : packageDetails[0].PackageName}
                </h2>
              </div>
              <div className="swiper mySwiper py-3  modal-swiper-gold">
                <Swiper
                  modules={[Navigation, Pagination, A11y, Autoplay]}
                  loop={false}
                  slidesPerView={
                    isPage ? 7.6 : Math.min(3.3, packageDetails.length)
                  }
                  spaceBetween={isPage ? 20 : 10}
                >
                  {packageDetails.map((service: PackageDetails) => (
                    <SwiperSlide
                      key={`package-service-${service.RecordId}`}
                      onClick={() => handleClick(service)}
                    >
                      <div
                        className={`am-service-cards p-2 bg-3 text-center ${
                          selectedService?.RecordId === service.RecordId
                            ? "active"
                            : ""
                        }`}
                      >
                        <div className="am-rounded-box rounded-circle d-flex align-items-center justify-content-center bg-3 mx-auto">
                          {/* <img src={service.imgUrl} alt={service.Name} /> */}
                          <img
                            src="https://goodhand.b-cdn.net/Assets/Immigration_new_Subcategory_Icon/New%20Entry%20Permits.svg"
                            alt=""
                          />
                        </div>
                        <h3 className="font-9 text-black ff-medium mt-2 mb-0 pk-cat-name">
                          {service.Name}
                        </h3>
                      </div>

                      <div></div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
            {selectedService && (
              <>
                <div
                  className="my-3 p-4 bg-2 rounded-3"
                  ref={serviceSelectedRef}
                >
                  <div className="container mt-4">
                    <p className="text-start font-14 text-black ff-semibold mb-0">
                      {selectedService.Name}
                    </p>
                    <p className="text-start font-14 text-primary ff-semibold">
                      <span className="ff-currency ff-medium font-16"></span>
                      {selectedService.totalPrice}
                    </p>
                    <div className="row">
                      <div className="timeline-container">
                        {selectedService.packageOptions.map(
                          (serviceOptions, index) => (
                            <div
                              key={`options-${serviceOptions.PriceRecordId}`}
                              className="timeline-card layout-border"
                            >
                              {/* Circle with Number */}
                              <div className="timeline-step-circle">
                                {index + 1}
                              </div>
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <div className="w-100">
                                  <div
                                    className={`timeline-title font-12 ff-semibold mb-2 serv-${serviceOptions.sub2SlugServiceSub1}`}
                                  >
                                    <div className="font-10 ff-bold">
                                      {serviceOptions.sub2ServiceName}
                                    </div>
                                  </div>
                                  <p className="font-14 ff-bold text-black mb-1">
                                    <span className="me-1">
                                      <svg
                                        width="17"
                                        height="17"
                                        viewBox="0 0 17 17"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M8.49987 0C9.16106 4.01899e-05 9.79961 0.240893 10.2961 0.677528L10.427 0.800792L11.0204 1.39416C11.1833 1.55594 11.3959 1.65808 11.624 1.68404L11.7387 1.69085H12.5888C13.2836 1.69081 13.952 1.95656 14.457 2.43361C14.962 2.91066 15.2654 3.56285 15.3049 4.25644L15.3092 4.41116V5.26126C15.3092 5.49078 15.3874 5.71436 15.5285 5.89288L15.605 5.97789L16.1975 6.57126C16.6886 7.05959 16.975 7.71687 16.9982 8.40906C17.0214 9.10125 16.7798 9.77624 16.3225 10.2964L16.1992 10.4273L15.6058 11.0207C15.4441 11.1835 15.3419 11.3962 15.316 11.6242L15.3092 11.739V12.5891C15.3092 13.2838 15.0434 13.9522 14.5664 14.4572C14.0893 14.9623 13.4371 15.2657 12.7436 15.3052L12.5888 15.3094H11.7387C11.5095 15.3095 11.287 15.3868 11.1071 15.5287L11.0221 15.6052L10.4287 16.1978C9.94041 16.6889 9.28313 16.9752 8.59094 16.9985C7.89875 17.0217 7.22376 16.78 6.70361 16.3227L6.5727 16.1995L5.97933 15.6061C5.81648 15.4443 5.60383 15.3422 5.37576 15.3162L5.261 15.3094H4.4109C3.71618 15.3095 3.04778 15.0437 2.54275 14.5667C2.03772 14.0896 1.73435 13.4374 1.69483 12.7438L1.69058 12.5891V11.739C1.69051 11.5098 1.61325 11.2873 1.47126 11.1074L1.39475 11.0224L0.802231 10.429C0.311114 9.94067 0.0247513 9.28339 0.00153081 8.5912C-0.0216897 7.89901 0.219981 7.22403 0.677267 6.70387L0.800531 6.57296L1.3939 5.97959C1.55568 5.81675 1.65782 5.60409 1.68378 5.37602L1.69058 5.26126V4.41116L1.69483 4.25644C1.73279 3.58949 2.01483 2.95983 2.4872 2.48746C2.95957 2.01509 3.58923 1.73305 4.25618 1.6951L4.4109 1.69085H5.261C5.4902 1.69077 5.71269 1.61351 5.89262 1.47152L5.97763 1.39501L6.571 0.802493C6.82376 0.548219 7.1243 0.346419 7.45533 0.208699C7.78635 0.0709776 8.14134 5.19206e-05 8.49987 0ZM11.6427 6.19041C11.4833 6.03105 11.2671 5.94152 11.0417 5.94152C10.8162 5.94152 10.6001 6.03105 10.4406 6.19041L7.64127 8.98894L6.54209 7.89061L6.46218 7.82005C6.29132 7.68794 6.07658 7.62581 5.86157 7.64629C5.64656 7.66677 5.44741 7.76833 5.30456 7.93033C5.16172 8.09233 5.08589 8.30262 5.09248 8.51851C5.09907 8.73439 5.18759 8.93967 5.34005 9.09265L7.04025 10.7928L7.12016 10.8634C7.28372 10.9903 7.48796 11.0531 7.69456 11.0401C7.90115 11.0271 8.09591 10.9392 8.24229 10.7928L11.6427 7.39245L11.7132 7.31254C11.8401 7.14898 11.903 6.94474 11.89 6.73815C11.877 6.53155 11.789 6.3368 11.6427 6.19041Z"
                                          fill="#385986"
                                        />
                                      </svg>
                                    </span>{" "}
                                    {serviceOptions.serviceName}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                      <div>
                        <div>
                          {serviceInformation && (
                            <ServiceInformation
                              serviceInformations={serviceInformation?.details}
                              sectionTitle={serviceInformation?.title}
                              line={serviceInformation?.line}
                            />
                          )}
                        </div>
                        {selectedService?.requireDocs && (
                          <ServiceRequiredDoc
                            requireDocs={nonImportantDocs}
                            importantNotes={importantDocs}
                            sectionTitle={sectionTitle || ""}
                            line={line || ""}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PackageSection;
